import type { ReactNode } from "react";
import { cn } from "@u/cn";
import InfoTooltip from "@ui/info-tooltip";

/**
 * Inline field-group divider: optional leading icon + mono uppercase
 * micro-label + trailing hairline, with an optional info tooltip. Groups
 * related fields inside a form card (price, location, dimensions…). For
 * public/marketing section headers use `SectionHeader`; for standalone form
 * labels use `FieldLabel`.
 */
export function SectionLabel({
  icon,
  label,
  info,
  trailing,
  labelClassName,
  hideRule,
}: {
  icon?: ReactNode;
  label: string;
  info?: string;
  /** Optional control after the hairline (e.g. a collapse chevron). */
  trailing?: ReactNode;
  /** Extra classes on the label text (e.g. `font-bold text-foreground`). */
  labelClassName?: string;
  /** Drop the trailing hairline (label reads as a plain heading). */
  hideRule?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 pt-2">
      {icon && <span className="text-muted-foreground shrink-0">{icon}</span>}
      <span
        className={cn(
          "text-xs font-mono uppercase tracking-widest text-foreground whitespace-nowrap",
          labelClassName,
        )}
      >
        {label}
      </span>
      {info && <InfoTooltip text={info} />}
      {hideRule ? <div className="flex-1" /> : <div className="flex-1 h-px bg-border/40" />}
      {trailing}
    </div>
  );
}
